import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Route } from 'lucide-react';

const CHIP_W = 152;
const CHIP_H = 42;

/** Flying chip to #seo-detail-page-tour-button after the Fix Issues detail tour ends. */
export default function SeoFixDetailTourReplayHintFlyout({ onFinished }) {
    const { t } = useTranslation();
    const finishedRef = useRef(false);
    const handleAnimationComplete = useCallback(() => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onFinished();
    }, [onFinished]);

    const [coords, setCoords] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const id = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (cancelled) return;
                const startLeft = window.innerWidth / 2 - CHIP_W / 2;
                const startTop = window.innerHeight / 2 - CHIP_H / 2;
                const el = document.getElementById('seo-detail-page-tour-button');
                let endLeft = startLeft;
                let endTop = startTop;
                if (el) {
                    const r = el.getBoundingClientRect();
                    endLeft = r.left + r.width / 2 - CHIP_W / 2;
                    endTop = r.top + r.height / 2 - CHIP_H / 2;
                }
                setCoords({ startLeft, startTop, endLeft, endTop });
            });
        });
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(id);
        };
    }, []);

    if (!coords) return null;

    return (
        <motion.div
            role="presentation"
            aria-hidden
            initial={{
                left: coords.startLeft,
                top: coords.startTop,
                scale: 1,
                opacity: 0.92,
            }}
            animate={{
                left: coords.endLeft,
                top: coords.endTop,
                scale: 0.88,
                opacity: 1,
            }}
            transition={{
                type: 'spring',
                stiffness: 420,
                damping: 32,
                mass: 0.75,
            }}
            onAnimationComplete={handleAnimationComplete}
            style={{
                position: 'fixed',
                zIndex: 12000,
                width: CHIP_W,
                minHeight: CHIP_H,
                pointerEvents: 'none',
                background: 'linear-gradient(135deg, #0d6efd 0%, #2563eb 100%)',
                color: '#fff',
                borderRadius: 10,
                boxShadow: '0 12px 40px rgba(15, 23, 42, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '0 10px',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '0.02em',
            }}
        >
            <Route size={14} aria-hidden style={{ flexShrink: 0, opacity: 0.95 }} />
            {t('seoFixDetailTour.pageTourButton')}
        </motion.div>
    );
}
